// Typed wrappers for the wave-3 endpoints: integrations, mappings, Inbox, incidents and pull
// request links (F06), browser test accounts / scenarios / runs / preview (F05) and project
// memory (F07). Paths and bodies mirror apps/api/src/routes/{integrations,browser,memory}.ts.
import { api } from "./client.ts";
import type {
  BrowserRun,
  BrowserScenario,
  BrowserSession,
  CreateIntegrationInput,
  CreatePreviewTargetInput,
  CreateTestAccountInput,
  InboxEventDetail,
  InboxEventSummary,
  InboxQuery,
  Incident,
  IncidentState,
  IngestInput,
  IngestResponse,
  GithubConnectionTest,
  GithubManifestStart,
  GithubRepository,
  Integration,
  IntegrationMapping,
  IntegrationSecretField,
  MemoryComparison,
  MemoryDataset,
  MemoryDatasetCase,
  MemoryEvaluation,
  MemoryEvaluationAccepted,
  MemoryIndexVersion,
  MemorySearchInput,
  MemorySearchResponse,
  MemorySourceDetail,
  MemorySourcesPage,
  MemoryStats,
  OwnershipProof,
  Page,
  PatchIntegrationInput,
  PatchTestAccountInput,
  PreviewResponse,
  PreviewTarget,
  PullRequestLink,
  ScenarioState,
  TestAccount,
  TombstoneResult,
} from "./types-wave3.ts";

const enc = encodeURIComponent;
const withSignal = (signal?: AbortSignal) => (signal ? { signal } : {});

// ---------- Integrations ----------

export const listIntegrations = (signal?: AbortSignal) => api<{ items: Integration[]; github?: { setup_allowed: boolean; public_url_configured: boolean } }>("/integrations", withSignal(signal));
export const getIntegration = (id: string, signal?: AbortSignal) => api<Integration>(`/integrations/${enc(id)}`, withSignal(signal));
export const createIntegration = (body: CreateIntegrationInput, idempotencyKey?: string) =>
  api<Integration>("/integrations", { method: "POST", body, ...(idempotencyKey ? { idempotencyKey } : {}) });
export const updateIntegration = (id: string, body: PatchIntegrationInput) => api<Integration>(`/integrations/${enc(id)}`, { method: "PATCH", body });
export const integrationStateAction = (id: string, action: "activate" | "suspend" | "disable", body: { expected_version: number; ownership_proof?: OwnershipProof }) =>
  api<Integration>(`/integrations/${enc(id)}/${action}`, { method: "POST", body });
export const putIntegrationSecret = (id: string, field: IntegrationSecretField, value: string) =>
  api<Integration>(`/integrations/${enc(id)}/secrets/${field}`, { method: "PUT", body: { value } });
export const revokeIntegrationSecret = (id: string, field: IntegrationSecretField) => api<Integration>(`/integrations/${enc(id)}/secrets/${field}`, { method: "DELETE" });
/** Always answers 409 BLOCKED-external in this wave (never connects). */
export const testIntegration = (id: string) => api<unknown>(`/integrations/${enc(id)}/test`, { method: "POST", body: {} });

// ---------- GitHub connections (D21 G1) ----------

export const startGithubManifest = (body: { owner_type: "user" | "organization"; owner_login?: string }) => api<GithubManifestStart>("/github/manifests", { method: "POST", body });
export const convertGithubManifest = (body: { code: string; state: string }) =>
  api<{ integration: Integration; install: { url: string; expires_at: string } }>("/github/manifests/conversions", { method: "POST", body });
export const createGithubInstallLink = (id: string) => api<{ url: string; expires_at: string }>(`/integrations/${enc(id)}/github/install-links`, { method: "POST", body: {} });
export const verifyGithubInstallation = (body: { installation_id: number; state: string }) => api<GithubConnectionTest>("/github/installations", { method: "POST", body });
export const listGithubRepositories = (id: string, signal?: AbortSignal) =>
  api<{ integration_id: string; total: number; repositories: GithubRepository[] }>(`/integrations/${enc(id)}/github/repositories`, withSignal(signal));

export const listMappings = (q: { integration_id?: string; project_id?: string }, signal?: AbortSignal) =>
  api<{ items: IntegrationMapping[] }>("/integration-mappings", { query: q, ...withSignal(signal) });
export const createMapping = (body: { integration_id: string; project_id: string; external_project_ref: string; environments: string[] }) =>
  api<IntegrationMapping>("/integration-mappings", { method: "POST", body });
export const updateMapping = (id: string, body: { state?: "active" | "disabled"; environments?: string[]; pr_delivery?: "on" | "off"; pr_max_per_hour?: number }) =>
  api<IntegrationMapping>(`/integration-mappings/${enc(id)}`, { method: "PATCH", body });
export const deleteMapping = (id: string) => api<void>(`/integration-mappings/${enc(id)}`, { method: "DELETE" });

// ---------- Inbox ----------

export const listInbox = (q: InboxQuery, signal?: AbortSignal) =>
  api<Page<InboxEventSummary>>("/inbox", {
    query: {
      state: q.state || undefined,
      integration_id: q.integration_id,
      project_id: q.project_id,
      unassigned: q.unassigned ? "true" : undefined,
      cursor: q.cursor ?? undefined,
      limit: q.limit,
    },
    ...withSignal(signal),
  });
export const getInboxEvent = (id: string, signal?: AbortSignal) => api<InboxEventDetail>(`/inbox/${enc(id)}`, withSignal(signal));
export const reprocessInboxEvent = (id: string) => api<InboxEventDetail>(`/inbox/${enc(id)}/reprocess`, { method: "POST", body: {} });
export const assignInboxEvent = (id: string, projectId: string) => api<InboxEventDetail>(`/inbox/${enc(id)}/assign`, { method: "POST", body: { project_id: projectId } });

// ---------- Incidents and pull request links ----------

export const listIncidents = (
  q: { project_id?: string; unassigned?: boolean; state?: IncidentState | ""; integration_id?: string; cursor?: string | null; limit?: number },
  signal?: AbortSignal,
) =>
  api<Page<Incident>>("/incidents", {
    query: { project_id: q.project_id, unassigned: q.unassigned ? "true" : undefined, state: q.state || undefined, integration_id: q.integration_id, cursor: q.cursor ?? undefined, limit: q.limit },
    ...withSignal(signal),
  });
export const listPullRequestLinks = (q: { project_id?: string; task_id?: string; cursor?: string | null; limit?: number }, signal?: AbortSignal) =>
  api<Page<PullRequestLink>>("/pull-request-links", { query: { project_id: q.project_id, task_id: q.task_id, cursor: q.cursor ?? undefined, limit: q.limit }, ...withSignal(signal) });

// ---------- Browser: test accounts, scenarios, runs, sessions, preview ----------

export const listTestAccounts = (projectId: string, signal?: AbortSignal) => api<{ items: TestAccount[] }>(`/projects/${enc(projectId)}/test-accounts`, withSignal(signal));
export const createTestAccount = (projectId: string, body: CreateTestAccountInput) => api<TestAccount>(`/projects/${enc(projectId)}/test-accounts`, { method: "POST", body });
export const updateTestAccount = (id: string, body: PatchTestAccountInput) => api<TestAccount>(`/test-accounts/${enc(id)}`, { method: "PATCH", body });
export const putTestAccountSecret = (id: string, field: "password" | "totp", value: string) => api<TestAccount>(`/test-accounts/${enc(id)}/secrets/${field}`, { method: "PUT", body: { value } });
export const revokeTestAccountTotp = (id: string) => api<TestAccount>(`/test-accounts/${enc(id)}/secrets/totp`, { method: "DELETE" });
export const disableTestAccount = (id: string) => api<TestAccount>(`/test-accounts/${enc(id)}`, { method: "DELETE" });

export const listScenarios = (projectId: string, q: { state?: ScenarioState | "" } = {}, signal?: AbortSignal) =>
  api<{ items: BrowserScenario[]; egress_control_label: string }>(`/projects/${enc(projectId)}/browser-scenarios`, { query: { state: q.state || undefined }, ...withSignal(signal) });
export const createScenario = (projectId: string, body: { environment_id: string; test_account_id: string | null; state: "draft" | "active"; body: Record<string, unknown> }) =>
  api<BrowserScenario>(`/projects/${enc(projectId)}/browser-scenarios`, { method: "POST", body });
export const setScenarioState = (id: string, state: ScenarioState) => api<BrowserScenario>(`/browser-scenarios/${enc(id)}`, { method: "PATCH", body: { state } });

export const listBrowserRuns = (projectId: string, signal?: AbortSignal) =>
  api<{ items: BrowserRun[]; egress_control_label: string }>(`/projects/${enc(projectId)}/browser-runs`, { query: { limit: 50 }, ...withSignal(signal) });
export const startBrowserRun = (projectId: string, body: { scenario_id: string; task_id: string | null; attempt_id: string | null }) =>
  api<BrowserRun>(`/projects/${enc(projectId)}/browser-runs`, { method: "POST", body });

export const listBrowserSessions = (projectId: string, signal?: AbortSignal) => api<{ items: BrowserSession[] }>(`/projects/${enc(projectId)}/browser-sessions`, withSignal(signal));
export const revokeBrowserSession = (id: string) => api<BrowserSession>(`/browser-sessions/${enc(id)}/revoke`, { method: "POST", body: {} });

export const getPreview = (projectId: string, signal?: AbortSignal) => api<PreviewResponse>(`/projects/${enc(projectId)}/preview`, withSignal(signal));
export const createPreviewTarget = (projectId: string, body: CreatePreviewTargetInput) => api<PreviewTarget>(`/projects/${enc(projectId)}/preview-targets`, { method: "POST", body });
export const capturePreview = (id: string, candidateHash: string | null) => api<PreviewTarget>(`/preview-targets/${enc(id)}/capture`, { method: "POST", body: { candidate_hash: candidateHash } });

// ---------- Memory ----------

export const listMemorySources = (
  projectId: string,
  q: { state?: string; kind?: string; current?: "true" | "false" | "all"; source_key?: string; cursor?: string | null; limit?: number },
  signal?: AbortSignal,
) => api<MemorySourcesPage>(`/projects/${enc(projectId)}/memory/sources`, { query: { ...q, cursor: q.cursor ?? undefined }, ...withSignal(signal) });
export const getMemorySource = (id: string, signal?: AbortSignal) => api<MemorySourceDetail>(`/memory-sources/${enc(id)}`, withSignal(signal));
export const ingestMemory = (projectId: string, body: IngestInput) => api<IngestResponse>(`/projects/${enc(projectId)}/memory/sources`, { method: "POST", body });
export const rejectMemorySource = (id: string, reason: string) => api<MemorySourceDetail>(`/memory-sources/${enc(id)}/reject`, { method: "POST", body: { reason } });
export const tombstoneMemorySource = (id: string, reason: string) => api<TombstoneResult>(`/memory-sources/${enc(id)}/tombstone`, { method: "POST", body: { reason } });

export const searchMemory = (projectId: string, body: MemorySearchInput) => api<MemorySearchResponse>(`/projects/${enc(projectId)}/memory/search`, { method: "POST", body });
export const getMemoryStats = (projectId: string, signal?: AbortSignal) => api<MemoryStats>(`/projects/${enc(projectId)}/memory/stats`, withSignal(signal));

export const listIndexVersions = (projectId: string, signal?: AbortSignal) => api<{ project_id: string; items: MemoryIndexVersion[] }>(`/projects/${enc(projectId)}/memory/index-versions`, withSignal(signal));
export const buildIndexVersion = (projectId: string, body: { strategy: string }) => api<MemoryIndexVersion>(`/projects/${enc(projectId)}/memory/index-versions`, { method: "POST", body });
export const evaluateIndexVersion = (id: string, body: { dataset_id: string; k: number }) => api<MemoryEvaluationAccepted>(`/memory-index-versions/${enc(id)}/evaluate`, { method: "POST", body });
export const compareIndexVersion = (id: string, body: { dataset_id: string; baseline_index_version_id?: string }) =>
  api<MemoryComparison>(`/memory-index-versions/${enc(id)}/compare`, { method: "POST", body });
export const activateIndexVersion = (id: string, expectedActiveVersionId: string | null) =>
  api<MemoryIndexVersion>(`/memory-index-versions/${enc(id)}/activate`, { method: "POST", body: { expected_active_version_id: expectedActiveVersionId } });
export const retireIndexVersion = (id: string) => api<MemoryIndexVersion>(`/memory-index-versions/${enc(id)}/retire`, { method: "POST", body: {} });
export const rollbackIndexVersion = (projectId: string, expectedActiveVersionId: string) =>
  api<MemoryIndexVersion>(`/projects/${enc(projectId)}/memory/index-versions/rollback`, { method: "POST", body: { expected_active_version_id: expectedActiveVersionId } });

export const listMemoryDatasets = (projectId: string, signal?: AbortSignal) => api<{ project_id: string; items: MemoryDataset[] }>(`/projects/${enc(projectId)}/memory/datasets`, withSignal(signal));
export const createMemoryDataset = (projectId: string, body: { name: string; cases: MemoryDatasetCase[] }) => api<MemoryDataset>(`/projects/${enc(projectId)}/memory/datasets`, { method: "POST", body });
export const setMemoryDatasetState = (id: string, state: "frozen" | "retired") => api<MemoryDataset>(`/memory-datasets/${enc(id)}`, { method: "PATCH", body: { state } });

export const listMemoryEvaluations = (projectId: string, q: { index_version_id?: string; dataset_id?: string } = {}, signal?: AbortSignal) =>
  api<{ project_id: string; items: MemoryEvaluation[] }>(`/projects/${enc(projectId)}/memory/evaluations`, { query: { ...q, limit: 50 }, ...withSignal(signal) });
export const runMemoryEvaluation = (projectId: string, body: { dataset_id: string; index_version_id?: string; k: number }) =>
  api<MemoryEvaluationAccepted>(`/projects/${enc(projectId)}/memory/evaluations`, { method: "POST", body });
